import { els } from './dom.js';
import {
  DEFAULT_VIDEO_TYPE,
  VIDEO_MODES,
  VIDEO_RULES,
  estimateVideoCredits,
  getAvailableModes,
  getImageLimit,
  getTimeValues,
  getVideoRule,
  normalizeVideoMode,
  shouldUseImageList,
} from './rules.js';
import { renderReferenceImages } from './result-view.js';

export function getSelectedVideoType() {
  return els.videoType()?.value || DEFAULT_VIDEO_TYPE;
}

export function getSelectedRule() {
  return getVideoRule(getSelectedVideoType());
}

export function getSelectedMode(state) {
  return normalizeVideoMode(getSelectedVideoType(), state?.mode || 'single');
}

export function imageLimit(state) {
  return getImageLimit(getSelectedVideoType(), getSelectedMode(state));
}

export function renderSelectOptions(select, values, { fallbackLabel = '自适应' } = {}) {
  if (!select) return;
  if (!values.length) {
    const fallback = document.createElement('option');
    fallback.value = '';
    fallback.textContent = fallbackLabel;
    select.replaceChildren(fallback);
    select.value = '';
    select.disabled = true;
    return;
  }
  select.disabled = false;
  const options = values.map((value) => {
    const label = value || fallbackLabel;
    const option = document.createElement('option');
    option.value = value;
    option.textContent = label;
    return option;
  });
  select.replaceChildren(...options);
}

export function renderTypeOptions() {
  const select = els.videoType();
  if (!select) return;
  const options = Object.entries(VIDEO_RULES).map(([value, rule]) => {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = rule.label;
    return option;
  });
  select.replaceChildren(...options);
  select.value = DEFAULT_VIDEO_TYPE;
}

export function renderModeControls(state, { onModeChange } = {}) {
  const type = getSelectedVideoType();
  const available = new Set(getAvailableModes(type));
  const current = normalizeVideoMode(type, state.mode);
  state.mode = current;
  els.modeButtons().forEach((btn) => {
    btn.onVideoModeChange = onModeChange;
    const mode = btn.dataset.videoMode;
    const active = mode === current;
    btn.classList.toggle('active', active);
    btn.disabled = !available.has(mode);
    btn.setAttribute('aria-selected', active ? 'true' : 'false');
    btn.textContent = VIDEO_MODES[mode]?.label || mode;
    if (!btn.dataset.boundModeClick) {
      btn.dataset.boundModeClick = '1';
      btn.addEventListener('click', () => {
        const nextMode = btn.dataset.videoMode;
        if (btn.disabled || !nextMode || nextMode === state.mode) return;
        state.mode = nextMode;
        btn.onVideoModeChange?.(nextMode);
      });
    }
  });
  const hint = els.modeHint();
  if (hint) hint.textContent = VIDEO_MODES[current]?.hint || '';
}

export function syncModelControls(state, { refreshSubmitState, onModeChange } = {}) {
  const type = getSelectedVideoType();
  const rule = getSelectedRule();
  renderModeControls(state, {
    onModeChange: (mode) => {
      syncModelControls(state, { refreshSubmitState, onModeChange });
      onModeChange?.(mode);
    },
  });
  const mode = getSelectedMode(state);
  const times = getTimeValues({ type, mode });
  renderSelectOptions(els.videoTime(), times.map(String));
  renderSelectOptions(els.resolution(), type === 'KLING' && mode === 'firstLast' ? ['1080p'] : (rule.resolutions || []));
  renderSelectOptions(els.ratio(), rule.ratios || []);

  const resolution = els.resolution();
  if (resolution && rule.defaultResolution && [...resolution.options].some((opt) => opt.value === rule.defaultResolution)) {
    resolution.value = rule.defaultResolution;
  }
  if (type === 'KLING' && mode === 'firstLast' && resolution) {
    resolution.value = '1080p';
  }

  const camera = els.camera();
  if (camera) {
    camera.disabled = type !== 'WAN';
    if (type !== 'WAN') camera.value = '';
  }

  const voice = els.voice();
  if (voice) {
    if (rule.voiceFixedOn) {
      voice.disabled = true;
      voice.checked = true;
    } else {
      const supportsVoice = !state.lastFrame && (
        type === 'WAN' || type === 'SEED' || type === 'SEED_FAST' || type === 'SEED_MINI' ||
        (type === 'KLING' && mode === 'single')
      );
      voice.disabled = !supportsVoice;
      if (voice.disabled) voice.checked = false;
    }
  }

  const referenceInput = els.referenceInput();
  if (referenceInput) referenceInput.multiple = shouldUseImageList(type, mode);

  const lastFrameField = els.lastFrameField();
  const lastFrameInput = els.lastFrameInput();
  const showLastFrame = mode === 'firstLast' && rule.supportsLastFrame;
  if (lastFrameField) lastFrameField.hidden = !showLastFrame;
  if (lastFrameInput) lastFrameInput.disabled = !showLastFrame;
  const label = els.lastFrameLabel();
  if (!showLastFrame) {
    state.lastFrame = null;
    if (lastFrameInput) lastFrameInput.value = '';
    if (label) label.textContent = mode === 'firstLast' ? '当前模型不支持尾帧' : '上传尾帧会关闭声音';
  } else if (label && !state.lastFrame) {
    label.textContent = '上传尾帧，生成时会自动关闭声音';
  }

  renderReferenceImages(state);
  renderCostEstimate(state);
  refreshSubmitState?.();
}

export function getCurrentCreditEstimate(state) {
  const mode = getSelectedMode(state);
  const voice = Boolean(els.voice()?.checked && !(mode === 'firstLast' && state.lastFrame));
  return estimateVideoCredits({
    type: getSelectedVideoType(),
    time: Number(els.videoTime()?.value || 10),
    resolution: els.resolution()?.value || '',
    mode,
    voice,
  });
}

export function renderCostEstimate(state) {
  const credits = getCurrentCreditEstimate(state);
  const box = els.costEstimate();
  if (box) {
    const label = document.createElement('span');
    label.textContent = '预计消耗';
    const value = document.createElement('strong');
    value.textContent = `${credits} 算力`;
    box.replaceChildren(label, value);
  }
  const btn = els.generateBtn();
  if (btn) btn.textContent = `立即生成（${credits} 算力）`;
}
